/**
 * 「邀请家人」卡（L 域 §5.1 · `AC-L1.5` / `AC-L1.6`）—— 邀请码的**唯一 UI 入口**
 *
 * 依据：L 域 PRD **§5.1**（邀请码 8 位 / 7 天 / **单码** / 重新生成即作废旧码）·
 * **`AC-L1.5`**（生成与轮换）· **`AC-L1.6`**（成员已满 4 人 ⇒ **409**）·
 * 契约 `contract/collaboration.ts`（`InviteView` · `expiresAt` 是 **UTC 毫秒**）。
 *
 * ## 🔴 四条判断
 *
 * | # | 判断 | 为什么 |
 * |:--:|---|---|
 * | 1 | **非 owner 不画按钮**（保留一句解释） | 邀请码是 `@RequireOwner(InviteMember)` ⇒ 画一个点了报 403 的按钮是骗人；但**整卡消失**会让成员以为"这个产品不能邀请" ⇒ 留一句「只有账本所有者能生成邀请码」 |
 * | 2 | 🔴 **满员 409 原样展示服务端 `message`** | 服务端返回的是「家庭账本最多 4 位成员」+ `details.memberCount`。端侧自己数人头会与服务端并发写入不一致（且规则两处实现，铁律 79） |
 * | 3 | **单码模型的代价必须写在按钮旁**（`INVITE_ROTATE_HINT`） | 用户不知道"重新生成会作废旧码"时，会在家人还在输入旧码的那一刻把码换掉 |
 * | 4 | **复制失败不静默** | `navigator.clipboard` 在 http / 老浏览器下不可用 ⇒ 退化为"选中文本 + 提示手动复制"，而不是点了没反应 |
 *
 * ## ⚠️ 过期文案的时钟
 *
 * `inviteExpiryText(invite, Date.now())` 在**渲染期**取当前时刻。
 * 🔴 它只影响"还剩 N 天"这一句**文案**，不参与任何业务判断（真正是否有效由服务端 `current()` 决定：
 * 过期码它就不会下发）—— 所以渲染期的时钟漂移不会造成错误状态。
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import type { LedgerRole } from '@jiaying/shared';
import { createLedgerInvite, currentLedgerInvite } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import {
  canManageCollab,
  inviteAlive,
  inviteCodeDisplay,
  inviteExpiryText,
} from '../features/collab.js';
import {
  INVITE_COPY_DONE,
  INVITE_EMPTY_HINT,
  INVITE_OWNER_ONLY_HINT,
  INVITE_ROTATE_HINT,
  INVITE_SECTION_TITLE,
  MEMBERS_CAP_HINT,
} from '../features/collab-labels.js';

function errorText(e: unknown): string {
  return e instanceof ApiError ? e.message : String(e);
}

export function InviteCard({ role }: { role: LedgerRole | string }): ReactElement {
  const isOwner = canManageCollab(role);
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);

  // 🔴 非 owner 不发请求（`enabled`）—— 服务端会 403，白跑一次没有意义
  const invite = useQuery({
    queryKey: ['ledgerInvite'],
    queryFn: currentLedgerInvite,
    enabled: isOwner,
    retry: false,
  });

  const generate = useMutation({
    mutationFn: createLedgerInvite,
    onSuccess: (data) => {
      qc.setQueryData(['ledgerInvite'], data);
      setCopied(false);
    },
  });

  const now = Date.now();
  const data = invite.data ?? null;
  const alive = data !== null && inviteAlive(data, now);

  async function copy(): Promise<void> {
    if (data === null) return;
    try {
      await navigator.clipboard?.writeText(data.code);
      setCopied(true);
    } catch {
      // 🔴 不静默：让用户知道要手动复制（码就在上面，可选中）
      setCopied(false);
    }
  }

  return (
    <Card data-testid="invite-card">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {INVITE_SECTION_TITLE}
        </Typography>

        {!isOwner && (
          <Typography variant="body2" color="text.secondary" data-testid="invite-owner-only">
            {INVITE_OWNER_ONLY_HINT}
          </Typography>
        )}

        {isOwner && (
          <>
            {invite.isPending && (
              <Typography variant="body2" color="text.secondary">
                正在读取邀请码…
              </Typography>
            )}

            {invite.isError && (
              <Alert severity="error" data-testid="invite-load-error">
                {errorText(invite.error)}
              </Alert>
            )}

            {invite.isSuccess && data === null && (
              <Typography variant="body2" color="text.secondary" data-testid="invite-empty">
                {INVITE_EMPTY_HINT}
              </Typography>
            )}

            {invite.isSuccess && data !== null && (
              <Box data-testid="invite-current">
                <Stack direction="row" alignItems="baseline" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <Typography
                    variant="h5"
                    sx={{ fontFamily: 'monospace', letterSpacing: 2 }}
                    data-testid="invite-code"
                  >
                    {inviteCodeDisplay(data.code)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={alive ? 'text.secondary' : 'error.main'}
                    data-testid="invite-expiry"
                  >
                    {inviteExpiryText(data, now)}
                  </Typography>
                </Stack>
              </Box>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              <Button
                variant={data === null ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  generate.mutate();
                }}
                disabled={generate.isPending}
                data-testid="invite-generate"
              >
                {generate.isPending ? '生成中…' : data === null ? '生成邀请码' : '重新生成'}
              </Button>
              {data !== null && (
                <Button size="small" onClick={() => void copy()} data-testid="invite-copy">
                  {copied ? INVITE_COPY_DONE : '复制邀请码'}
                </Button>
              )}
            </Stack>

            {data !== null && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                {INVITE_ROTATE_HINT}
              </Typography>
            )}

            {generate.isError && (
              <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="invite-generate-error">
                {/* 🔴 409 满员 / 403 无权限：**原样展示** */}
                {errorText(generate.error)}
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              {MEMBERS_CAP_HINT}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
}
